import {
  Button,
  Card,
  Divider,
  message,
  Table,
  Tooltip,
  Form,
  Row,
  Col,
  DatePicker,
  Radio,
} from "antd"
import * as API from "common/api"
import * as COMMONS from "common/common"
import moment from "moment"
import "moment/locale/ja"
import React, { useEffect, useRef, useState } from "react"
import { Helmet } from "react-helmet-async"
import { FaEye, FaEyeSlash } from "react-icons/fa"
import { useQuery } from "react-query"
import { useNavigate } from "react-router-dom"
import styled from "styled-components"
import useResizeObserver from "@react-hook/resize-observer"

moment.updateLocale("ja")

const CustomDatePicker = styled(DatePicker)`
  .ant-picker-input > input {
    text-align: center;
  }
`

const CustomTable = styled(Table)`
  .ant-table-selection-extra {
    position: initial;
    margin: 0;
    padding: 0;
  }

  .ant-table-ping-right,
  .ant-table-ping-left tr th.ant-table-cell {
    cursor: grab;
    cursor: -webkit-grab;
  }

  tr.active th.ant-table-cell {
    cursor: grabbing;
    cursor: -webkit-grabbing;
  }
`

const useSize = (target) => {
  const [size, setSize] = React.useState()

  React.useLayoutEffect(() => {
    setSize(target.current.getBoundingClientRect())
  }, [target])

  useResizeObserver(target, (entry) => setSize(entry.contentRect))
  return size
}

const ProductStock = (props) => {
  const navigate = useNavigate()

  const isMountedRef = COMMONS.USE_IS_MOUNTED_REF()
  const [purchaseFilterForm] = Form.useForm()

  const tableRef = useRef(null)
  const size = useSize(tableRef)
  const [isScrollableHintVisible, setIsScrollableHintVisible] = useState(false)

  const [productStock, setProductStock] = useState([])
  const [filterOrderDateFrom, setFilterOrderDateFrom] = useState(
    moment().startOf("month"),
  )
  const [filterOrderDateTo, setFilterOrderDateTo] = useState(
    moment().endOf("month"),
  )
  const [filterIsDisplayed, setFilterIsDisplayed] = useState(true)

  const [paginationSort, setPaginationSort] = useState("asc")
  const [paginationSortKey, setPaginationSortKey] = useState("productId")

  useQuery(
    [
      API.QUERY_KEY_ADMIN_PRODUCT_STOCK,
      {
        filterOrderDateFrom: filterOrderDateFrom,
        filterOrderDateTo: filterOrderDateTo,
        filterIsDisplayed: filterIsDisplayed,
        paginationSortKey: paginationSortKey,
        paginationSort: paginationSort,
      },
    ],
    () =>
      API.ADMIN_GET_PRODUCT_STOCK(
        filterOrderDateFrom,
        filterOrderDateTo,
        filterIsDisplayed,
        paginationSort,
        paginationSortKey,
      ),
    {
      keepPreviousData: true,

      onSuccess: (response) => {
        if (isMountedRef.current) {
          if (response?.data?.rows && response?.data?.rows?.length > 0) {
            setProductStock(response.data.rows)
          } else {
            setProductStock([])
          }
          if (response?.data?.from) {
            purchaseFilterForm.setFieldsValue({
              filterOrderDateFrom: moment(response.data.from),
            })
          }
          if (response?.data?.to) {
            purchaseFilterForm.setFieldsValue({
              filterOrderDateTo: moment(response.data.to),
            })
          }

          setPaginationSortKey(response?.data?.sortKey || "productId")
          setPaginationSort(response?.data?.sort || "asc")
        }
      },
      onError: (error) => {
        if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
          navigate(COMMONS.PERMISSION_ERROR_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
          message.warning(COMMONS.ERROR_SESSION_MSG)
          navigate(COMMONS.ADMIN_LOGIN_ROUTE)
        } else if (
          error?.response?.status === COMMONS.RESPONSE_CONFLICT_ERROR
        ) {
          message.warning(COMMONS.WARN_DATE_OVER_YEAR)
        } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        } else {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        }
      },
    },
  )

  useEffect(() => {
    const slider = document.querySelector("thead.ant-table-thead tr")
    const body = document.querySelector(".ant-table-container .ant-table-body")
    let isDown = false
    let startX
    let scrollLeft

    slider.addEventListener("mousedown", (e) => {
      isDown = true
      slider.classList.add("active")
      startX = e.pageX - body.offsetLeft
      scrollLeft = body.scrollLeft
    })

    slider.addEventListener("mouseleave", () => {
      isDown = false
      slider.classList.remove("active")
    })

    slider.addEventListener("mouseup", () => {
      isDown = false
      slider.classList.remove("active")
    })

    slider.addEventListener("mousemove", (e) => {
      if (!isDown) {
        return
      }

      e.preventDefault()
      const x = e.pageX - body.offsetLeft
      const walk = (x - startX) * 3 //scroll-fast
      body.scrollLeft = scrollLeft - walk
    })
  }, [])

  useEffect(() => {
    if (
      tableRef?.current?.querySelector(".ant-table-ping-right") ||
      tableRef?.current?.querySelector(".ant-table-ping-left")
    ) {
      setIsScrollableHintVisible(true)
    } else {
      setIsScrollableHintVisible(false)
    }
  }, [size])

  const productStockColumn = [
    {
      key: "表示",
      title: <p className="font-bold bg-secondary rounded">表示</p>,
      dataIndex: "isDisplayed",
      align: "center",
      width: 50,
      render: (isDisplayed) =>
        isDisplayed ? (
          <FaEye className="text-primary text-lg" />
        ) : (
          <FaEyeSlash className="text-red-600 text-lg" />
        ),
    },
    {
      key: "productId",
      title: "ID",
      dataIndex: "productId",
      align: "center",
      width: 50,
      sorter: () => {},
    },
    {
      key: "productCode",
      title: <p className="whitespace-pre-wrap">{`商品\nコード`}</p>,
      dataIndex: "productCode",
      align: "center",
      width: 100,
      sorter: () => {},
    },
    {
      key: "supplierProductNo",
      title: <p className="whitespace-pre-wrap">{`品番`}</p>,
      dataIndex: "supplierProductNo",
      align: "center",
      width: 100,
      sorter: () => {},
    },

    {
      key: "categoryName",
      title: "部門名",
      dataIndex: "categoryName",
      align: "center",
      width: 110,
      sorter: () => {},
      render: (Category) =>
        Category?.categoryName ? Category.categoryName : "ー",
    },
    {
      key: "productName",
      title: "商品名",
      dataIndex: "productName",
      align: "center",
      width: 150,
      sorter: () => {},
    },

    {
      key: "cost",
      title: "原価",
      dataIndex: "cost",
      align: "center",
      width: 80,
      sorter: () => {},
      render: (productCost) =>
        productCost ? `￥${Number(productCost).toLocaleString()}` : 0,
    },
    {
      key: "price",
      title: "商品単価",
      dataIndex: "price",
      align: "center",
      width: 80,
      sorter: () => {},
      render: (productPrice) =>
        productPrice ? `￥${Number(productPrice).toLocaleString()}` : 0,
    },
    {
      key: "stockAmount",
      title: <p>{`在庫数 （理論在庫）`}</p>,
      dataIndex: "stockAmount",
      align: "center",
      width: 80,
      sorter: () => {},
      render: (Inventory) =>
        Inventory?.stockAmount
          ? `${Number(Inventory.stockAmount).toLocaleString()}`
          : 0,
    },
    {
      key: "layawayStockAmount",
      title: <p>{`店舗受取取置在庫数`}</p>,
      dataIndex: "layawayStockAmount",
      align: "center",
      width: 80,
      sorter: () => {},
      render: (Inventory) =>
        Inventory?.layawayStockAmount
          ? `${Number(Inventory.layawayStockAmount).toLocaleString()}`
          : 0,
    },
    {
      key: "unshippedAmount",
      title: <p>{`配達受取未出荷在庫数`}</p>,
      dataIndex: "unshippedAmount",
      align: "center",
      width: 80,
      // sorter: () => {},
      render: (unshippedAmount) =>
        unshippedAmount ? `${Number(unshippedAmount).toLocaleString()}` : 0,
    },

    {
      key: "inCompleteAmount",
      title: <p>{`API取置在庫数（リンク発行中）`}</p>,
      dataIndex: "inCompleteAmount",
      align: "center",
      width: 80,
      // sorter: () => {},
      render: (inCompleteAmount) =>
        inCompleteAmount ? `${Number(inCompleteAmount).toLocaleString()}` : 0,
    },
    {
      key: "sumAmount",
      title: <p>{`在庫数（合計）`}</p>,
      dataIndex: "sumAmount",
      align: "center",
      width: 80,
      // sorter: () => {},
      render: (inventorySumAmount) =>
        inventorySumAmount
          ? `${Number(inventorySumAmount).toLocaleString()}`
          : "ー",
    },
  ]

  const handleTableChange = (paginationData, filters, sorter) => {
    if (sorter && sorter.order) {
      if (sorter.order === "ascend") {
        if (sorter.order !== paginationSort) {
          setPaginationSort("asc")
        }
      } else if (sorter.order === "descend") {
        if (sorter.order !== paginationSort) {
          setPaginationSort("desc")
        }
      }
    }

    if (sorter && sorter.field) {
      if (sorter.field !== paginationSortKey) {
        setPaginationSortKey(sorter.field)
      }
    }
  }

  const handleFilter = (data) => {
    if (data.filterOrderDateFrom !== filterOrderDateFrom) {
      setFilterOrderDateFrom(data.filterOrderDateFrom)
    }
    if (data.filterOrderDateTo !== filterOrderDateTo) {
      setFilterOrderDateTo(data.filterOrderDateTo)
    }
    if (data.filterIsDisplayed !== filterIsDisplayed) {
      setFilterIsDisplayed(data.filterIsDisplayed)
    }
  }

  return (
    <>
      <Helmet>
        <title>在庫状況 | {process.env.REACT_APP_SYSTEM_NAME}</title>
      </Helmet>
      <Card
        title="在庫状況"
        className="animate__animated animate__fadeIn"
        bordered={false}
      >
        <Row>
          <Col xs={24}>
            <Form
              form={purchaseFilterForm}
              onFinish={handleFilter}
              size="large"
              initialValues={{
                filterOrderDateFrom: undefined,
                filterOrderDateTo: undefined,
                filterPurchaseStatus: undefined,
                filterIsDisplayed: true,
              }}
            >
              <Row gutter={[16, 16]} justify="start">
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  xl={{ span: 6 }}
                >
                  <Form.Item
                    help="開始日から"
                    name="filterOrderDateFrom"
                    className="mb-4"
                  >
                    <CustomDatePicker className="w-full" inputReadOnly />
                  </Form.Item>
                </Col>
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  xl={{ span: 6 }}
                >
                  <Form.Item
                    help="終了日まで"
                    name="filterOrderDateTo"
                    className="mb-4 w-full"
                  >
                    <CustomDatePicker className="w-full" inputReadOnly />
                  </Form.Item>
                </Col>
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  lg={{ span: 6 }}
                  xl={{ span: 6 }}
                >
                  <p>※期間は1年以内</p>
                </Col>
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  // lg={{ span: 8 }}
                  // xl={{ span: 6 }}
                >
                  <Form.Item
                    help="端末表示"
                    name="filterIsDisplayed"
                    className="mb-4"
                  >
                    <Radio.Group>
                      {COMMONS.IS_DISPLAYED_TYPES.map((idt) => (
                        <Radio.Button key={idt.value} value={idt.value}>
                          {idt.text}
                        </Radio.Button>
                      ))}
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[8, 8]} justify="center mt-4">
                <Col>
                  <Button type="primary" htmlType="submit" className="px-12">
                    検索
                  </Button>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
        <Divider />

        <Tooltip
          visible={isScrollableHintVisible}
          placement="topRight"
          title="⇦スクロールできます⇨"
          overlayStyle={{
            zIndex: 999,
          }}
        >
          <CustomTable
            ref={tableRef}
            bordered
            size="small"
            columns={productStockColumn}
            locale={{ emptyText: "配達がありません" }}
            onChange={handleTableChange}
            scroll={{
              x: 640,
              y: 720,
            }}
            pagination={false}
            dataSource={
              productStock
                ? productStock.map((product) => {
                    return {
                      key: product?.productId,
                      isDisplayed: product.isDisplayed,
                      productId: product?.productId || "ー",
                      productCode: product?.productCode || "ー",
                      categoryName: product?.Category,
                      productName: product?.productName || "ー",
                      cost: product?.cost,
                      price: product?.price,
                      stockAmount: product?.Inventory,
                      layawayStockAmount: product?.Inventory,
                      unshippedAmount: product?.unshippedAmount,
                      inCompleteAmount: product?.incompleteAmount,
                      supplierProductNo: product?.supplierProductNo || "ー",
                      sumAmount: product?.sumAmount,
                    }
                  })
                : []
            }
          />
        </Tooltip>
      </Card>
    </>
  )
}

export default ProductStock
